import { Injectable } from '@angular/core';
import { DeviceBusinessInfo, DeviceType } from './device-detection.interface';

/**
 * Strategy interface for layout-specific business logic
 */
export interface LayoutStrategy {
    getBusinessInfo(deviceType: DeviceType): DeviceBusinessInfo;
}

/**
 * Base strategy with common logic shared by most layouts
 */
class BaseLayoutStrategy implements LayoutStrategy {
    getBusinessInfo(deviceType: DeviceType): DeviceBusinessInfo {
        return {
            type: deviceType,
            isHamburgerMenuVisible: deviceType !== DeviceType.desktop,
            shouldShowMobileSearchPanel: deviceType === DeviceType.mobile,
            needsRepositioning: true,
            shouldCloseHamburgerMenu: deviceType === DeviceType.desktop,
            shouldUpdateMenuCollapseService: true,
            shouldHideMobileSearchPanel: deviceType === DeviceType.desktop
        };
    }
}

/**
 * Layout-9 specific strategy (has unique logic)
 */
class Layout9Strategy implements LayoutStrategy {
    getBusinessInfo(deviceType: DeviceType): DeviceBusinessInfo {
        return {
            type: deviceType,
            isHamburgerMenuVisible: deviceType !== DeviceType.desktop,
            shouldShowMobileSearchPanel: deviceType === DeviceType.mobile,
            needsRepositioning: true,
            shouldCloseHamburgerMenu: deviceType === DeviceType.desktop,
            shouldUpdateMenuCollapseService: true
            // Note: No shouldHideMobileSearchPanel for layout-9
        };
    }
}

/**
 * Strategy registry that manages all layout strategies in one place
 */
@Injectable({
    providedIn: 'root'
})
export class LayoutStrategiesRegistry {
    private strategies = new Map<string, LayoutStrategy>();
    private baseStrategy = new BaseLayoutStrategy();

    constructor() {
        this.initializeStrategies();
    }

    private initializeStrategies(): void {
        // Layouts 1-8 use the same base strategy
        for (let i = 1; i <= 8; i++) {
            this.strategies.set(`layout-${i}`, this.baseStrategy);
        }

        // Layout-9 has unique logic
        this.strategies.set('layout-9', new Layout9Strategy());
    }

    /**
     * Get strategy for a specific layout
     */
    getStrategy(layoutId: string): LayoutStrategy {
        return this.strategies.get(layoutId) || this.baseStrategy;
    }

    /**
     * Get business info for a specific layout
     */
    getBusinessInfo(layoutId: string, deviceType: DeviceType): DeviceBusinessInfo {
        const strategy = this.getStrategy(layoutId);
        return strategy.getBusinessInfo(deviceType);
    }

    /**
     * Check if layout exists
     */
    hasLayout(layoutId: string): boolean {
        return this.strategies.has(layoutId);
    }

    /**
     * Get all registered layout IDs
     */
    getRegisteredLayouts(): string[] {
        return Array.from(this.strategies.keys());
    }
}
